'use client';

import { useCallback, useMemo, useState, type FormEvent } from 'react';

import { WIDGETS_ASSET_HOST } from '@/shared/config/assets';
import { useCart } from '@/features/cart';
import { useSearchSuggestions } from '@/features/search-bar';
import { NavbarProvider, type NavbarContextValue } from './header/context';
import Header from './header/Header';
import { PassCodeRail } from './PassCodeRail';
import type { SearchComboboxConfig } from '@/shared/components/search-combobox';
import type {
  NavDropdownItem as HeaderDropdownItem,
  NavItem as HeaderNavItem,
  NavMegaMenu,
  NavMegaMenuGroup,
} from '../types/navigation';
import type {
  NavbarWidgetProps,
  LegacyNavItem,
  NavItem,
  NavItemBadge,
  NavItemBadgeVariant,
  NavSubitem,
  NavSubitemGroup,
  NavbarAccountLink,
  NavbarNavClickItem,
} from '../types/navbar';

const DEFAULT_HOME_URL = '/';
const DEFAULT_LOGIN_URL = '/login';
const DEFAULT_REGISTER_URL = '/registro';
const DEFAULT_ACCOUNT_URL = '/mi-perfil';
const DEFAULT_CLASSROOM_URL = '/mi-aula';
const DEFAULT_SEARCH_BASE_URL = '/?s=';
const EMPTY_SEARCH_PREVIEW_ITEMS: SearchComboboxConfig['previewItems'] = [];

const LOGO_BY_COUNTRY = {
  AR: { height: 203, src: `${WIDGETS_ASSET_HOST}/images/logo-adipa-ar.webp`, width: 753 },
  CL: { height: 355, src: `${WIDGETS_ASSET_HOST}/images/logo-adipa.png`, width: 1776 },
  CO: { height: 65, src: `${WIDGETS_ASSET_HOST}/images/logo-colombia.webp`, width: 241 },
  MX: { height: 203, src: `${WIDGETS_ASSET_HOST}/images/logo-adipa-mx.webp`, width: 753 },
} as const;

const BADGE_COLOR_BY_VARIANT: Record<NavItemBadgeVariant, HeaderNavItem['badgeColor']> = {
  error: 'magenta',
  neutral: 'brand',
  primary: 'brand',
  secondary: 'brand',
  success: 'green',
  warning: 'magenta',
};

const isLegacyNavItem = (item: NavItem): item is LegacyNavItem => 'url' in item;

const normalizeGroupId = (value: string, index: number) => {
  const normalized = value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || `group-${index + 1}`;
};

const normalizeLegacyBadge = (badge?: NavItemBadge) => {
  if (!badge) return {};

  return {
    badge: badge.label,
    badgeColor: BADGE_COLOR_BY_VARIANT[badge.variant],
  };
};

const normalizeDropdownItems = (items: readonly NavSubitem[]): HeaderDropdownItem[] =>
  items.map((item) => ({
    href: item.url,
    label: item.label,
  }));

const normalizeLegacyMegaMenu = (
  label: string,
  groups: readonly NavSubitemGroup[],
): NavMegaMenu => {
  const normalizedGroups: NavMegaMenuGroup[] = groups.map((group, index) => {
    const groupLabel = group.groupLabel ?? label;

    return {
      id: normalizeGroupId(groupLabel, index),
      label: groupLabel,
      links: group.items.map((item) => ({
        href: item.url,
        label: item.label,
      })),
    };
  });

  return {
    categories: normalizedGroups.map((group) => ({
      label: group.label,
      sectionId: group.id,
    })),
    defaultGroupId: normalizedGroups[0]?.id,
    groups: normalizedGroups,
  };
};

const normalizeNavItem = (item: NavItem): HeaderNavItem => {
  if (!isLegacyNavItem(item)) {
    return item;
  }

  const normalized: HeaderNavItem = {
    active: item.active,
    highlight: item.highlight,
    href: item.url,
    label: item.label,
    ...normalizeLegacyBadge(item.badge),
  };

  if (item.subitemGroups?.length) {
    normalized.megaMenu = normalizeLegacyMegaMenu(item.label, item.subitemGroups);
    return normalized;
  }

  if (item.subitems?.length) {
    normalized.dropdown = normalizeDropdownItems(item.subitems);
  }

  return normalized;
};

const normalizeNavItems = (items: readonly NavItem[]): HeaderNavItem[] =>
  items.map(normalizeNavItem);

export const buildAccountLinks = ({
  accountLinks,
  accountUrl,
  classroomBadge,
  classroomUrl,
  favoritesUrl,
  labBadge,
  labUrl,
}: {
  accountLinks?: readonly NavbarAccountLink[];
  accountUrl: string;
  classroomBadge?: string;
  classroomUrl: string;
  favoritesUrl?: string;
  labBadge?: string;
  labUrl?: string;
}): NavbarAccountLink[] => {
  if (accountLinks?.length) return [...accountLinks];

  return [
    {
      href: classroomUrl,
      icon: 'book',
      label: 'Mi aula',
      ...(classroomBadge ? { badge: classroomBadge, badgeColor: 'magenta' as const } : {}),
    },
    { href: accountUrl, icon: 'user', label: 'Mi perfil' },
    ...(favoritesUrl ? [{ href: favoritesUrl, icon: 'heart' as const, label: 'Mis favoritos' }] : []),
    ...(labUrl
      ? [
          {
            href: labUrl,
            icon: 'brain' as const,
            label: 'Adipa Lab',
            ...(labBadge ? { badge: labBadge, badgeColor: 'magenta' as const } : {}),
          },
        ]
      : []),
  ];
};

export function NavbarWidget({
  accountLinks,
  accountUrl = DEFAULT_ACCOUNT_URL,
  authState: authStateProp,
  cartItemCount,
  cartLoading = false,
  classroomBadge,
  classroomUrl = DEFAULT_CLASSROOM_URL,
  country = 'CL',
  favoritesCount = 0,
  favoritesPulseKey,
  favoritesUrl,
  hideUserActions = false,
  labBadge,
  labUrl,
  hideLogin = false,
  homeUrl = DEFAULT_HOME_URL,
  loginUrl = DEFAULT_LOGIN_URL,
  logoUrl,
  logoutUrl,
  navItems,
  pathname,
  onCartClick,
  onLoginClick,
  onLogoutClick,
  onNavClick,
  onSearch,
  profileUrl,
  registerUrl = DEFAULT_REGISTER_URL,
  searchAutoSuggest = false,
  searchBaseUrl = DEFAULT_SEARCH_BASE_URL,
  searchDebounceMs,
  searchMaxResults,
  searchMinLength,
  searchPlaceholder = '¿Qué quieres aprender?',
  searchPreviewItems,
  searchSuggestUrl,
  showSearch = true,
  sticky = true,
  user,
}: NavbarWidgetProps) {
  const [query, setQuery] = useState('');
  const storeCartCount = useCart((s) => s.items.length);
  const openCart = useCart((s) => s.setIsOpen);
  const resolvedCartCount = cartItemCount ?? storeCartCount;
  const authState = authStateProp ?? (user ? 'authenticated' : 'guest');
  const resolvedLogo = logoUrl
    ? { height: LOGO_BY_COUNTRY.CL.height, src: logoUrl, width: LOGO_BY_COUNTRY.CL.width }
    : LOGO_BY_COUNTRY[country];
  const resolvedAccountUrl = profileUrl ?? accountUrl;
  const resolvedUser = authState === 'authenticated' ? (user ?? null) : null;

  const normalizedNavItems = useMemo(
    () => (navItems ? normalizeNavItems(navItems) : undefined),
    [navItems],
  );

  const handleLogout = useCallback(async () => {
    if (onLogoutClick) {
      await onLogoutClick();
    }

    if (logoutUrl && typeof window !== 'undefined') {
      window.location.href = logoutUrl;
    }
  }, [logoutUrl, onLogoutClick]);

  const handleCartClick = useCallback(async () => {
    openCart(true);
    if (onCartClick) {
      await onCartClick();
    }
  }, [onCartClick, openCart]);

  const handleSearchSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>, nextQuery?: string) => {
      event.preventDefault();
      const trimmedQuery = (nextQuery ?? query).trim();
      if (!trimmedQuery) return;

      if (onSearch) {
        onSearch(trimmedQuery);
        return;
      }

      if (typeof window !== 'undefined') {
        window.location.href = `${searchBaseUrl}${encodeURIComponent(trimmedQuery)}`;
      }
    },
    [onSearch, query, searchBaseUrl],
  );

  const autoSuggestActive = searchAutoSuggest && !searchPreviewItems;
  const { items: autoSuggestItems, isLoading: autoSuggestIsLoading } = useSearchSuggestions(
    autoSuggestActive ? query : '',
    {
      debounceMs: searchDebounceMs,
      maxResults: searchMaxResults,
      minLength: searchMinLength,
      suggestUrl: searchSuggestUrl,
    },
  );

  const resolvedPreviewItems =
    searchPreviewItems ?? (autoSuggestActive ? autoSuggestItems : EMPTY_SEARCH_PREVIEW_ITEMS);

  const search = useMemo<SearchComboboxConfig>(
    () => ({
      emptyStateLabel: 'No encontramos coincidencias todavía.',
      isLoading: autoSuggestActive ? autoSuggestIsLoading : false,
      onPreviewSelect: (item) => {
        if (item.href && typeof window !== 'undefined') {
          window.location.href = item.href;
        }
      },
      onQueryChange: setQuery,
      onSubmit: handleSearchSubmit,
      placeholder: searchPlaceholder,
      previewEnabled: Boolean(searchPreviewItems || autoSuggestActive),
      previewItems: resolvedPreviewItems,
      query,
    }),
    [
      autoSuggestActive,
      autoSuggestIsLoading,
      handleSearchSubmit,
      query,
      resolvedPreviewItems,
      searchPlaceholder,
      searchPreviewItems,
    ],
  );

  const handleNavClick = useCallback(
    (item: NavbarNavClickItem) => {
      onNavClick?.(item);
    },
    [onNavClick],
  );

  const contextValue = useMemo<NavbarContextValue>(
    () => ({
      accountLinks: buildAccountLinks({
        accountLinks,
        accountUrl: resolvedAccountUrl,
        classroomBadge,
        classroomUrl,
        favoritesUrl,
        labBadge,
        labUrl,
      }),
      accountUrl: resolvedAccountUrl,
      cartItemCount: resolvedCartCount,
      cartLoading,
      classroomBadge,
      classroomUrl,
      favoritesCount,
      favoritesPulseKey,
      favoritesUrl,
      hideUserActions,
      hideLogin,
      homeUrl,
      loginUrl,
      onCartClick: handleCartClick,
      onLogin: onLoginClick,
      onLogout: handleLogout,
      onNavClick: handleNavClick,
      registerUrl,
      user: resolvedUser,
    }),
    [
      accountLinks,
      cartLoading,
      classroomBadge,
      classroomUrl,
      favoritesCount,
      favoritesPulseKey,
      favoritesUrl,
      handleCartClick,
      handleLogout,
      handleNavClick,
      hideUserActions,
      hideLogin,
      homeUrl,
      labBadge,
      labUrl,
      loginUrl,
      onLoginClick,
      registerUrl,
      resolvedAccountUrl,
      resolvedCartCount,
      resolvedUser,
    ],
  );

  return (
    <NavbarProvider value={contextValue}>
      <Header
        logoHref={homeUrl}
        logoHeight={resolvedLogo.height}
        logoSrc={resolvedLogo.src}
        logoWidth={resolvedLogo.width}
        navItems={normalizedNavItems}
        pathname={pathname}
        search={search}
        showSearch={showSearch}
        sticky={sticky}
      />
      <PassCodeRail />
    </NavbarProvider>
  );
}

export { NavbarWidget as Navbar };
